import { AUTH_BROADCAST_CHANNEL, AUTH_LOCK_WAIT_TIMEOUT_MS } from '@/constants/auth';

let channel = null;
let handlers = {};
let signOutInProgress = false;
const inFlight = new Map();

export function connect({ onSignIn, onSignOut, onTokensUpdated } = {}) {
  handlers = { onSignIn, onSignOut, onTokensUpdated };
  if (!('BroadcastChannel' in window)) return;
  channel?.close(); // idempotent: drop a channel left by a previous connect (HMR / re-init)
  channel = new BroadcastChannel(AUTH_BROADCAST_CHANNEL);
  channel.onmessage = ({ data }) => {
    if (data?.type === 'signIn') handlers.onSignIn?.();
    if (data?.type === 'signOut') handlers.onSignOut?.();
    if (data?.type === 'tokensUpdated') handlers.onTokensUpdated?.(data.bpId);
  };
}

export function announceSignIn() {
  channel?.postMessage({ type: 'signIn' });
}
export function announceSignOut() {
  channel?.postMessage({ type: 'signOut' });
}
export function announceTokensUpdated(bpId) {
  channel?.postMessage({ type: 'tokensUpdated', bpId });
}

export function setSignOutInProgress(v) {
  signOutInProgress = v;
}

// Best-effort snapshot of who holds / awaits this lock, for the timeout log. Never throws.
async function describeLock(name) {
  try {
    const snapshot = await navigator.locks.query();
    const pick = (list) =>
      (list || [])
        .filter((entry) => entry.name === name)
        .map(({ name: lockName, mode, clientId }) => ({ name: lockName, mode, clientId }));
    return { held: pick(snapshot?.held), pending: pick(snapshot?.pending) };
  } catch {
    return { held: null, pending: null };
  }
}

// Web Locks request that gives up waiting after timeoutMs instead of hanging forever behind a
// lock another browsing context never releases. The timeout only bounds the wait for the grant;
// once granted, fn runs to completion.
function requestWithTimeout(name, fn, timeoutMs) {
  const controller = new AbortController();
  let granted = false;
  let settled = false;
  let timedOut = false;

  const timer = setTimeout(async () => {
    if (granted || settled) return;
    const { held, pending } = await describeLock(name);
    if (granted || settled) return;
    console.error('[auth] lock wait timed out', { name, timeoutMs, held, pending });
    timedOut = true;
    controller.abort();
  }, timeoutMs);

  return navigator.locks
    .request(name, { signal: controller.signal }, (lock) => {
      granted = true;
      clearTimeout(timer);
      return fn(lock);
    })
    .catch((error) => {
      // A raw AbortError would be silently dropped by the alerts store, so surface a real error.
      if (timedOut && !granted && error?.name === 'AbortError') {
        const timeoutError = new Error(`Timed out waiting for auth lock ${name}`);
        timeoutError.code = 'ERR_AUTH_LOCK_TIMEOUT';
        throw timeoutError;
      }
      throw error;
    })
    .finally(() => {
      settled = true;
      clearTimeout(timer);
    });
}

// navigator.locks when available (cross-tab dedup, bounded wait); else in-tab promise dedup.
export async function withRefreshLock(name, fn, { timeoutMs = AUTH_LOCK_WAIT_TIMEOUT_MS } = {}) {
  if (navigator.locks?.request) return requestWithTimeout(name, fn, timeoutMs);
  if (!inFlight.has(name)) {
    inFlight.set(
      name,
      Promise.resolve()
        .then(fn)
        .finally(() => inFlight.delete(name)),
    );
  }
  return inFlight.get(name);
}

export const crossTabSync = {
  connect,
  announceSignIn,
  announceSignOut,
  announceTokensUpdated,
  setSignOutInProgress,
  withRefreshLock,
  get signOutInProgress() {
    return signOutInProgress;
  },
};
